import React, { useEffect, useState } from 'react';
import { Sparkles, Save, Check } from 'lucide-react';
import { Card, CardContent, Button } from '@components/ui';
import { useTasteProfile } from '@hooks/useTasteProfile';

const PLACEHOLDER = `e.g.

VOICE & PHRASING
- gravelly baritone, weary defiance; never breathy or pop-bright
- avoid "baby/girl/boy" lyric crutches
- conversational > poetic; show, don't tell

THEMES I LIKE
- working-class small-town characters with quiet pride
- weather as emotional weather (rain = grief, heat = tension)
- specific places + brand names over generic imagery

THEMES TO AVOID
- party / club / club drugs
- aspirational wealth, luxury cars
- cartoon villainy

REFERENCE ARTISTS
- Drive-By Truckers, Jason Isbell, Lucero — for narrative discipline
- Allman Brothers, Mastodon — for arrangement weight
- avoid Imagine Dragons / Maroon 5 phrasing patterns`;

export const TasteProfileSection: React.FC = () => {
  const { profile, loading, saving, error, save } = useTasteProfile();
  const [localContent, setLocalContent] = useState('');
  const [localEnabled, setLocalEnabled] = useState(true);
  const [justSaved, setJustSaved] = useState(false);

  useEffect(() => {
    if (profile) {
      setLocalContent(profile.content);
      setLocalEnabled(profile.enabled);
    }
  }, [profile]);

  const dirty = profile ? (profile.content !== localContent || profile.enabled !== localEnabled) : localContent.length > 0;
  const charCount = localContent.length;
  const overLimit = charCount > 8000;

  const handleSave = async () => {
    if (overLimit) return;
    const ok = await save(localContent, localEnabled);
    if (ok) {
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    }
  };

  return (
    <Card variant="outlined" className="border-accent/30 bg-accent/5">
      <CardContent>
        <div className="flex items-start gap-3">
          <Sparkles className="text-accent shrink-0 mt-0.5" size={18} />
          <div className="space-y-4 flex-1 min-w-0">
            <div className="space-y-1">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-bold text-slate-200">Personal Taste Profile</h3>
                <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer">
                  <span>Inject</span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={localEnabled}
                    onClick={() => setLocalEnabled(!localEnabled)}
                    className={`relative w-9 h-5 rounded-full transition-colors ${localEnabled ? 'bg-accent' : 'bg-slate-700'}`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-slate-950 transition-transform ${localEnabled ? 'translate-x-4' : ''}`}
                    />
                  </button>
                </label>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed">
                Prepended to every LLM call (Studio Team, RiffWriter, Narrative Engine) as preserved constraints. Use it to capture your voice preferences, recurring themes, banned tropes, and reference artists.
              </p>
            </div>

            <textarea
              value={localContent}
              onChange={(e) => setLocalContent(e.target.value)}
              placeholder={PLACEHOLDER}
              disabled={loading}
              rows={14}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-xs text-slate-200 font-mono focus:outline-none focus:border-accent resize-y placeholder:text-slate-600 disabled:opacity-50"
            />

            <div className="flex items-center justify-between gap-3 text-[11px]">
              <span className={overLimit ? 'text-red-400' : 'text-slate-500'}>
                {charCount.toLocaleString()} / 8,000 chars
              </span>
              <div className="flex items-center gap-2">
                {error && <span className="text-red-400">{error}</span>}
                <Button
                  variant={justSaved ? 'success' : 'primary'}
                  size="sm"
                  onClick={handleSave}
                  isLoading={saving}
                  loadingText="Saving..."
                  disabled={!dirty || overLimit || loading}
                  leftIcon={justSaved ? <Check size={12} /> : <Save size={12} />}
                >
                  {justSaved ? 'Saved' : 'Save Profile'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
